import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Heart,
  Pencil,
  Trash2,
  Flag,
} from "lucide-react";
import { getFullName } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { collectReferencesFromMarkdown } from "@/lib/contentReferences";
import EmojiInsertButton from "./EmojiInsertButton";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import MarkdownBody from "./MarkdownBody";
import CommentMedia from "./CommentMedia";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMarkdownEditor";
import { formatPostDateTime } from "@/lib/formatRelativeTime";
import { useSocialPaths } from "@/lib/socialPaths";
import { useIsMobile } from "@/hooks/use-mobile";
import SocialReportDialog from "./SocialReportDialog";
import VerifiedOrgMark from "./VerifiedOrgMark";
import MediaAttachBar from "./MediaAttachBar";
import { locationWithFocus } from "@/lib/permalinks";

function socialCommentIdentity(comment, paths) {
  const actorOrg = comment?.actor_org || comment?.actorOrg || null;
  if (actorOrg?.username || actorOrg?.display_name) {
    return {
      displayName: actorOrg.display_name || actorOrg.username || "Organization",
      handle: actorOrg.username || null,
      isOrg: true,
      profileHref: actorOrg.username ? paths.org(actorOrg.username) : null,
      avatarUrl: actorOrg.avatar_url || actorOrg.avatarUrl || null,
      orgForAvatar: {
        name: actorOrg.display_name || actorOrg.username,
        avatar_url: actorOrg.avatar_url || actorOrg.avatarUrl || null,
      },
      actorOrg,
    };
  }
  const social = comment?.social_author || comment?.socialAuthor || null;
  const user = comment?.user || {};
  const displayName =
    social?.display_name ||
    user.display_name ||
    comment?.author?.display_name ||
    comment?.author?.name ||
    social?.username ||
    user.username ||
    "Unknown";
  const handle = social?.username || user.username || comment?.author?.username || null;
  return {
    displayName,
    handle,
    isOrg: false,
    profileHref: handle ? paths.user(handle) : null,
    avatarUrl: social?.avatar_url || user.avatar_url || null,
    orgForAvatar: null,
    actorOrg: null,
  };
}

const ThreadedComments = ({
  comments = [],
  onReply,
  onLike,
  onEdit,
  onDelete,
  currentUserId,
  likingCommentIds = new Set(),
  depth = 0,
  orgId,
  accessToken,
  membersById,
  /** Workplace only — Community Space must not open org member profiles */
  enableMemberProfiles = true,
  relativeTimestamps = true,
  /** Social / community: "This comment was deleted" (future: moderator removal) */
  socialTombstone = false,
  /** Parent social post id — enables Report on comments */
  postId = null,
  /**
   * Optional override for Continue thread (e.g. CardInspect without URL focus).
   * When omitted, navigates to the same path with ?focus=:commentId.
   */
  onContinueThread = null,
  /**
   * Extra comment id to scroll/highlight (in addition to ?comment=), e.g. after
   * leaving local focus mode in CardInspect.
   */
  scrollToCommentId = null,
}) => {
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = useSocialPaths();
  const isMobile = useIsMobile();
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyGif, setReplyGif] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const replyEditorRef = useRef(null);
  const editEditorRef = useRef(null);
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const [reportTarget, setReportTarget] = useState(null);

  const handleContinueThread = (commentId) => {
    if (commentId == null) return;
    if (typeof onContinueThread === "function") {
      onContinueThread(commentId);
      return;
    }
    navigate(locationWithFocus(location, commentId));
  };

  const referenceContext = useMemo(
    () =>
      orgId && accessToken
        ? {
            enabled: true,
            orgId,
            accessToken,
            contextType: "comment",
          }
        : null,
    [orgId, accessToken]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const commentParam = params.get("comment") || scrollToCommentId;
    if (!commentParam) return;
    setHighlightId(String(commentParam));
    const t = window.setTimeout(() => {
      const el = document.getElementById(`comment-${commentParam}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    const clear = window.setTimeout(() => setHighlightId(null), 2200);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(clear);
    };
  }, [location.search, comments, scrollToCommentId]);

  // Initialize all comments (at all depths) as expanded by default
  useEffect(() => {
    const allCommentIds = comments
      .map((comment) => comment.id || comment.interaction_id || comment.interactionId)
      .filter(Boolean);
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      allCommentIds.forEach((id) => next.add(id));
      return next;
    });
  }, [comments]);

  const toggleThread = (commentId) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const resolveCommentMember = (comment) => {
    const raw = comment?.user || comment?.author;
    if (!raw) return null;
    const uid =
      raw.user_id || raw.userId || comment.user_id || comment.userId;
    if (!uid) return null;
    const member = membersById?.[uid];
    return {
      ...raw,
      user_id: uid,
      title: member?.title || raw.title || comment.author?.role || null,
      org_email: member?.org_email ?? raw.org_email ?? null,
      member_avatar_url:
        raw.member_avatar_url ||
        member?.member_avatar_url ||
        member?.user?.member_avatar_url ||
        null,
    };
  };

  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyContent("");
    setReplyGif(null);
    setEditingId(null);
  };

  const handleEditClick = (comment) => {
    const commentId = comment.id || comment.interaction_id || comment.interactionId;
    setEditingId(commentId);
    setEditContent(comment.content || "");
    setReplyingTo(null);
    setReplyGif(null);
  };

  const handleSubmitReply = async (e) => {
    e?.preventDefault?.();
    const body = normalizeEditorMarkdown(replyContent);
    if ((isMarkdownEmpty(body) && !replyGif) || !replyingTo || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onReply(replyingTo, body, {
        references: collectReferencesFromMarkdown(body, orgId),
        gif: replyGif,
      });
      setReplyContent("");
      setReplyGif(null);
      replyEditorRef.current?.clear();
      setReplyingTo(null);
    } catch (error) {
      console.error("Failed to post reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e?.preventDefault?.();
    const body = normalizeEditorMarkdown(editContent);
    if (isMarkdownEmpty(body) || !editingId || !onEdit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onEdit(editingId, body, {
        references: collectReferencesFromMarkdown(body, orgId),
      });
      setEditingId(null);
      setEditContent("");
      editEditorRef.current?.clear();
    } catch (error) {
      console.error("Failed to edit comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async (commentId) => {
    if (!onDelete) return;
    const ok = await confirm({
      title: "Delete comment?",
      description: "Delete this comment? Replies will be kept.",
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await onDelete(commentId);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  };

  if (comments.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No comments yet. Be the first to comment!
      </div>
    );
  }

  const countAllReplies = (comment) => {
    if (!comment.replies || comment.replies.length === 0) return 0;
    return (
      comment.replies.length +
      comment.replies.reduce((sum, reply) => sum + countAllReplies(reply), 0)
    );
  };

  const threadLineColor = "rgba(128, 128, 128, 0.4)";
  /**
   * Nesting uses a tighter footprint (smaller avatar + gap + padding) so deep
   * threads stay readable. Stem/elbow geometry tracks the parent row size.
   * Depth 0: p-3 + half h-8 → 1.75rem center; content offset −1.75rem.
   * Depth ≥1: p-2 + half h-6 → 1.25rem center; parent offset −1.125rem when parent nested.
   */
  const isNested = depth > 0;
  const parentNested = depth > 1;
  const avatarClass = isNested ? "h-6 w-6 shrink-0" : "h-8 w-8 shrink-0";
  const avatarFallbackClass = isNested ? "text-[10px]" : "text-xs";
  const avatarCenterFromBox = isNested ? "1.25rem" : "1.75rem";
  const avatarCenterFromContent = parentNested ? "-1.125rem" : "-1.75rem";
  const elbowWidth = parentNested ? "1.75rem" : "2.5rem";
  const rowPadGap = isNested
    ? "flex items-start gap-1.5 p-2 rounded-md transition-colors cursor-pointer"
    : "flex items-start gap-3 p-3 rounded-md transition-colors cursor-pointer";
  const replyFormInset = isNested ? "ml-8 mr-2 mb-2" : "ml-11 mr-3 mb-3";

  return (
    <>
    <div className={cn(isNested ? "space-y-2" : "space-y-4")}>
      {comments.map((comment) => {
        const commentId =
          comment.id || comment.interaction_id || comment.interactionId;
        const hasReplies = comment.replies && comment.replies.length > 0;
        const isExpanded = expandedThreads.has(commentId);
        const isReplying = replyingTo === commentId;
        const isEditing = editingId === commentId;
        const isCollapsed = !isExpanded;
        const isLiking = likingCommentIds.has(commentId);
        const isLiked = !!comment.isLiked;
        const likeCount = comment.likeCount ?? 0;
        const isDeleted = Boolean(comment.deletedAt || comment.deleted_at);
        const editedAt = comment.editedAt || comment.edited_at;
        const authorUserId =
          comment.userId ||
          comment.user_id ||
          comment.user?.user_id ||
          comment.user?.userId;
        const isOwner =
          currentUserId && authorUserId && currentUserId === authorUserId;
        const deletedByName = socialTombstone
          ? socialCommentIdentity(comment, paths).displayName
          : getFullName(comment.deletedBy || comment.deleted_by) ||
            getFullName(comment.user) ||
            comment.author?.name ||
            "Unknown";
        const identity = socialCommentIdentity(comment, paths);
        const {
          displayName: socialDisplayName,
          handle: socialHandle,
          isOrg: isOrgActor,
          profileHref,
          avatarUrl: identityAvatarUrl,
          orgForAvatar,
          actorOrg,
        } = identity;
        // Desktop: full inline nesting. Mobile: recurse through depth 0→1→2 only;
        // at depth 2, deeper replies use Continue thread → ?focus=.
        const showChildThread =
          hasReplies &&
          isExpanded &&
          !isCollapsed &&
          (!isMobile || depth < 2);
        const showContinueThread =
          isMobile &&
          hasReplies &&
          isExpanded &&
          !isCollapsed &&
          depth === 2;
        const replyCount = hasReplies ? countAllReplies(comment) : 0;

        return (
          <div
            key={commentId}
            id={commentId != null ? `comment-${commentId}` : undefined}
            data-comment-id={commentId}
            className={cn(
              "relative rounded-md transition-colors",
              String(highlightId) === String(commentId) &&
                "bg-primary/10 ring-1 ring-primary/30"
            )}
          >
            {/* Stem: parent PFP center → last direct reply */}
            {showChildThread && (
              <div
                aria-hidden
                className="pointer-events-none absolute bottom-3 z-0 w-px -translate-x-1/2"
                style={{
                  left: avatarCenterFromBox,
                  top: avatarCenterFromBox,
                  backgroundColor: threadLineColor,
                }}
              />
            )}

            {/* Elbow from parent stem → child PFP vertical center */}
            {depth > 0 && (
              <div
                aria-hidden
                className="pointer-events-none absolute top-0 z-0 rounded-bl-[10px] border-b border-l"
                style={{
                  left: avatarCenterFromContent,
                  width: elbowWidth,
                  height: avatarCenterFromBox,
                  borderColor: threadLineColor,
                }}
              />
            )}

            <div
              className={cn(
                rowPadGap,
                "hover:bg-muted/50",
                depth > 0 && "bg-muted/10"
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  e.target.closest("button") ||
                  e.target.closest("form") ||
                  e.target.closest("textarea") ||
                  e.target.closest("[data-member-profile-trigger]") ||
                  e.target.closest("a")
                ) {
                  return;
                }
                toggleThread(commentId);
              }}
            >
              {enableMemberProfiles && !isOrgActor ? (
                <MemberProfileHover
                  user={resolveCommentMember(comment)}
                  orgId={orgId}
                >
                  <span data-member-profile-trigger className="relative z-[1]">
                    <OrgMemberAvatar
                      user={comment.user || comment.author}
                      className={avatarClass}
                      fallbackClassName={avatarFallbackClass}
                    />
                  </span>
                </MemberProfileHover>
              ) : isOrgActor ? (
                profileHref ? (
                  <Link to={profileHref} className="relative z-[1] shrink-0">
                    <OrganizationAvatar
                      org={orgForAvatar}
                      className={isNested ? "h-6 w-6" : "h-8 w-8"}
                      fallbackClassName={avatarFallbackClass}
                    />
                  </Link>
                ) : (
                  <OrganizationAvatar
                    org={orgForAvatar}
                    className={avatarClass}
                    fallbackClassName={avatarFallbackClass}
                  />
                )
              ) : (
                <SocialUserAvatar
                  user={comment.user || comment.author}
                  avatarUrl={identityAvatarUrl}
                  displayName={socialDisplayName}
                  className={avatarClass}
                  fallbackClassName={avatarFallbackClass}
                  to={profileHref}
                />
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {enableMemberProfiles && !isOrgActor ? (
                      <MemberProfileHover
                        user={resolveCommentMember(comment)}
                        orgId={orgId}
                      >
                        <span
                          data-member-profile-trigger
                          className="font-medium text-sm text-foreground hover:underline"
                        >
                          {getFullName(comment.user) ||
                            comment.author?.name ||
                            "Unknown"}
                        </span>
                      </MemberProfileHover>
                    ) : (
                      <div className="min-w-0 leading-tight">
                        {profileHref ? (
                          <Link
                            to={profileHref}
                            className="font-medium text-sm text-foreground truncate hover:underline inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="truncate">{socialDisplayName}</span>
                            {isOrgActor ? (
                              <VerifiedOrgMark org={actorOrg} />
                            ) : null}
                          </Link>
                        ) : (
                          <p className="font-medium text-sm text-foreground truncate inline-flex items-center gap-1">
                            <span className="truncate">{socialDisplayName}</span>
                            {isOrgActor ? (
                              <VerifiedOrgMark org={actorOrg} />
                            ) : null}
                          </p>
                        )}
                        {socialHandle ? (
                          profileHref ? (
                            <Link
                              to={profileHref}
                              className="text-xs text-muted-foreground hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              @{socialHandle}
                            </Link>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              @{socialHandle}
                            </span>
                          )
                        ) : null}
                      </div>
                    )}
                    {enableMemberProfiles && comment.author?.role && (
                      <span className="text-xs text-muted-foreground">
                        • {comment.author.role}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatPostDateTime(
                        comment.created_at || comment.createdAt,
                        { relative: relativeTimestamps, compact: relativeTimestamps }
                      )}
                    </span>
                    {editedAt && !isDeleted && (
                      <span className="text-xs text-muted-foreground italic">
                        • edited
                      </span>
                    )}
                    {isCollapsed && hasReplies && (
                      <span className="text-xs text-muted-foreground">
                        • {replyCount}{" "}
                        {replyCount === 1 ? "reply" : "replies"}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleThread(commentId);
                    }}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                  </Button>
                </div>

                {!isCollapsed && (
                  <>
                    {isDeleted ? (
                      <p className="text-sm text-muted-foreground italic mb-3">
                        {socialTombstone
                          ? "This comment was deleted"
                          : `Deleted by ${deletedByName}`}
                      </p>
                    ) : isEditing ? (
                      <form
                        onSubmit={handleSubmitEdit}
                        className="mb-3 space-y-2"
                      >
                        <RichMarkdownEditor
                          ref={editEditorRef}
                          value={editContent}
                          onChange={setEditContent}
                          onSubmit={handleSubmitEdit}
                          submitMode="mod-enter"
                          disabled={isSubmitting}
                          minRows={isMobile ? 2 : 3}
                          toolbar={isMobile ? "never" : "always"}
                          placeholder="Edit comment…"
                          referenceContext={referenceContext}
                        />
                        <div className="flex items-center justify-between gap-2">
                          <EmojiInsertButton
                            onInsert={(native) =>
                              editEditorRef.current?.insertText(native)
                            }
                            disabled={isSubmitting}
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditContent("");
                              }}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              size="sm"
                              disabled={
                                isMarkdownEmpty(editContent) || isSubmitting
                              }
                            >
                              {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </form>
                    ) : (
                      <div className="mb-3">
                        <MarkdownBody
                          content={comment.content}
                          compact
                          membersById={membersById}
                        />
                        {Array.isArray(comment.media) && comment.media[0] ? (
                          <CommentMedia
                            item={comment.media[0]}
                            isSocial={socialTombstone}
                            accessToken={accessToken}
                            orgId={orgId}
                          />
                        ) : null}
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!onLike || isLiking}
                        onClick={(e) => {
                          e.stopPropagation();
                          onLike?.(commentId);
                        }}
                        className="h-7 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Heart
                          className={cn(
                            "h-3 w-3 mr-1",
                            isLiking && "animate-pulse",
                            isLiked && "text-red-500"
                          )}
                          fill={isLiked ? "red" : "none"}
                        />
                        {likeCount > 0 ? likeCount : "Like"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReplyClick(commentId)}
                        className="h-7 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Reply
                      </Button>
                      {isOwner && !isDeleted && onEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(comment);
                          }}
                          className="h-7 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      {isOwner && !isDeleted && onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(commentId);
                          }}
                          className="h-7 text-xs text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      )}
                      {socialTombstone &&
                        accessToken &&
                        postId &&
                        !isDeleted && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReportTarget(commentId);
                            }}
                            className="h-7 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <Flag className="h-3 w-3 mr-1" />
                            Report
                          </Button>
                        )}
                    </div>
                  </>
                )}

                {showChildThread && (
                  <div className="relative mt-3">
                    <ThreadedComments
                      comments={comment.replies}
                      onReply={onReply}
                      onLike={onLike}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      likingCommentIds={likingCommentIds}
                      depth={depth + 1}
                      orgId={orgId}
                      accessToken={accessToken}
                      membersById={membersById}
                      enableMemberProfiles={enableMemberProfiles}
                      relativeTimestamps={relativeTimestamps}
                      socialTombstone={socialTombstone}
                      postId={postId}
                      onContinueThread={onContinueThread}
                      scrollToCommentId={scrollToCommentId}
                    />
                  </div>
                )}

                {showContinueThread && (
                  <div className="mt-2 pl-1">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContinueThread(commentId);
                      }}
                    >
                      Continue thread ({replyCount})
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Outside row click target so emoji picker / composer cannot collapse the thread */}
            {!isCollapsed && isReplying && (
              <form
                onSubmit={handleSubmitReply}
                className={cn(
                  replyFormInset,
                  "mt-0 space-y-2 bg-muted/30 rounded-md p-3"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <RichMarkdownEditor
                  ref={replyEditorRef}
                  value={replyContent}
                  onChange={setReplyContent}
                  onSubmit={handleSubmitReply}
                  submitMode="mod-enter"
                  placeholder="Write a reply..."
                  disabled={isSubmitting}
                  minRows={2}
                  toolbar={isMobile ? "never" : "always"}
                  referenceContext={referenceContext}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <MediaAttachBar
                    className="min-w-0"
                    mode="comment"
                    pendingFiles={[]}
                    onFilesChange={() => {}}
                    gif={replyGif}
                    onGifChange={setReplyGif}
                    allowGiphy
                    showAttachButton={false}
                    social={socialTombstone}
                    orgId={orgId}
                    disabled={isSubmitting}
                    trailingActions={
                      <EmojiInsertButton
                        onInsert={(native) =>
                          replyEditorRef.current?.insertText(native)
                        }
                        disabled={isSubmitting}
                      />
                    }
                  />
                  <div className="flex gap-2 shrink-0 ml-auto">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent("");
                        setReplyGif(null);
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={
                        (isMarkdownEmpty(replyContent) && !replyGif) ||
                        isSubmitting
                      }
                    >
                      {isSubmitting ? "Posting..." : "Post Reply"}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        );
      })}
    </div>
      {accessToken && postId && socialTombstone ? (
        <SocialReportDialog
          open={reportTarget != null}
          onOpenChange={(o) => {
            if (!o) setReportTarget(null);
          }}
          accessToken={accessToken}
          targetKind="comment"
          postId={postId}
          interactionId={reportTarget}
        />
      ) : null}
    </>
  );
};

export default ThreadedComments;
